import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";

import { BrowsingAction } from "@/pages/browser-defender/demo/passwordMisuseUpdated/service/types";

type AllowBrowsingDialogProps = {
  open: boolean;
  domainName: string;
  onClose: () => void;
  onConfirm: (action: BrowsingAction) => void;
  selectedUrls?: string[];
  selectedAlertIds?: number[];
};

export const AllowBrowsingDialog = ({
  open,
  domainName,
  onClose,
  onConfirm,
  selectedUrls = [],
  selectedAlertIds = [],
}: AllowBrowsingDialogProps) => {
  const { t } = useTranslation();
  const [scope, setScope] = useState<"domain" | "selected">("domain");
  const [reason, setReason] = useState<string>("");
  const [turnOffMonitoring, setTurnOffMonitoring] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      setScope(selectedAlertIds.length > 0 ? "selected" : "domain");
    } else {
      setReason("");
      setTurnOffMonitoring(false);
    }
  }, [open, selectedAlertIds]);

  const handleConfirm = () => {
    onConfirm({
      action: turnOffMonitoring ? "allow" : "reuse",
      domain: domainName,
      alertIds: scope === "selected" ? selectedAlertIds : undefined,
      urls: scope === "selected" ? selectedUrls : undefined,
      reason: reason,
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle sx={{ fontWeight: "bold", py: 2 }}>
        {t("Allow browsing for everyone on")}
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ py: 3 }}>
        <FormControl component="fieldset" sx={{ width: "100%" }}>
          <RadioGroup value={scope}>
            <FormControlLabel
              value="domain"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body" fontWeight="medium">
                    {t("Whole domain")}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    display="block"
                  >
                    {domainName}
                  </Typography>
                </Box>
              }
              disabled
              checked={scope === "domain"}
            />
            <FormControlLabel
              value="selected"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body" fontWeight="medium">
                    {t("Selected URLs only")}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    display="block"
                  >
                    {selectedAlertIds.length > 0
                      ? `${selectedAlertIds.length} URL(s) selected`
                      : t("No URL selected")}
                  </Typography>
                </Box>
              }
              disabled
              checked={scope === "selected"}
            />
          </RadioGroup>

          <FormControlLabel
            control={
              <Checkbox
                checked={turnOffMonitoring}
                onChange={(e) => setTurnOffMonitoring(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                {t("And turn off password reuse monitoring")}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  component="span"
                  sx={{ ml: 1 }}
                >
                  ({t("Optional")})
                </Typography>
              </Typography>
            }
            sx={{ mt: 2 }}
          />

          <Typography variant="body2" sx={{ mt: 3, mb: 1 }} fontWeight="medium">
            {t("Reason to allow browsing")}
            <Typography
              variant="body2"
              color="text.secondary"
              component="span"
              sx={{ ml: 1 }}
            >
              ({t("Optional")})
            </Typography>
          </Typography>

          <RadioGroup
            value={reason}
            onChange={(e) => {
              if (reason === e.target.value) {
                setReason("");
              } else {
                setReason(e.target.value);
              }
            }}
          >
            <FormControlLabel
              value="internal"
              control={<Radio size="small" />}
              label={t("Internal domain/URLs")}
            />
            <FormControlLabel
              value="external"
              control={<Radio size="small" />}
              label={t("External safe service")}
            />
            <FormControlLabel
              value="other"
              control={<Radio size="small" />}
              label={t("Other")}
            />
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", py: 2, px: 2 }}>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          fullWidth
          sx={{ borderRadius: "4px" }}
        >
          {t("Confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

type BlockBrowsingDialogProps = {
  open: boolean;
  domainName: string;
  onClose: () => void;
  onConfirm: (action: BrowsingAction) => void;
  selectedUrls?: string[];
  selectedAlertIds?: number[];
};

export const BlockBrowsingDialog = ({
  open,
  domainName,
  onClose,
  onConfirm,
  selectedUrls = [],
  selectedAlertIds = [],
}: BlockBrowsingDialogProps) => {
  const { t } = useTranslation();
  const [scope, setScope] = useState<"domain" | "selected">("domain");

  useEffect(() => {
    if (open) {
      setScope(selectedAlertIds.length > 0 ? "selected" : "domain");
    }
  }, [open, selectedAlertIds]);

  const handleConfirm = () => {
    onConfirm({
      action: "block",
      domain: domainName,
      alertIds: scope === "selected" ? selectedAlertIds : undefined,
      urls: scope === "selected" ? selectedUrls : undefined,
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle sx={{ fontWeight: "bold", py: 2 }}>
        {t("Block browsing for everyone on")}
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ py: 3 }}>
        <FormControl component="fieldset" sx={{ width: "100%" }}>
          <RadioGroup value={scope}>
            <FormControlLabel
              value="domain"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body" fontWeight="medium">
                    {t("Whole domain")}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    display="block"
                  >
                    {domainName}
                  </Typography>
                </Box>
              }
              disabled
              checked={scope === "domain"}
            />
            <FormControlLabel
              value="selected"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body" fontWeight="medium">
                    {t("Selected URLs only")}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    display="block"
                  >
                    {selectedAlertIds.length > 0
                      ? `${selectedAlertIds.length} URL(s) selected`
                      : t("No URL selected")}
                  </Typography>
                </Box>
              }
              disabled
              checked={scope === "selected"}
            />
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", py: 2, px: 2 }}>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          fullWidth
          sx={{ borderRadius: "4px" }}
        >
          {t("Confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
